import { useState, useEffect } from "react";
import { getCardById, PokemonCard } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Heart } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { addCardToCollection, auth, getUserCollection, toggleFavorite } from "@/lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "@/lib/firebase";

interface CardDetailProps {
  cardId: string;
}

export function CardDetail({ cardId }: CardDetailProps) {
  const [card, setCard] = useState<PokemonCard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { toast } = useToast();
  
  // Check if the card is already in the user's collection
  const checkIfCardInCollection = async (userId: string, cardId: string) => {
    try {
      const collectionRef = collection(db, "users", userId, "collection");
      const q = query(collectionRef, where("cardId", "==", cardId));
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error("Error checking if card is in collection:", error);
      return false;
    }
  };
  
  useEffect(() => {
    const fetchCard = async () => {
      setIsLoading(true);
      const cardData = await getCardById(cardId);
      setCard(cardData);
      setIsLoading(false);
    };
    
    fetchCard();
    
    // Check auth status and if the card is in the collection
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsLoggedIn(!!user);
      
      if (user) {
        const isInCollection = await checkIfCardInCollection(user.uid, cardId);
        setIsSaved(isInCollection);
      } else {
        setIsSaved(false);
      }
    });
    
    return () => unsubscribe();
  }, [cardId]);
  
  const saveToCollection = async () => {
    if (!card) return;
    
    if (!isLoggedIn) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to save cards to your collection.",
        variant: "destructive",
      });
      return;
    }
    
    setIsUpdating(true);
    try {
      // Prepare the card data to be stored
      const cardDataForCollection = {
        name: card.name,
        imageUrl: card.images.small,
        set: card.set.name,
        number: card.number,
        rarity: card.rarity || "Unknown",
        price: getCardPrice(),
        setTotal: card.set.printedTotal,
        id: card.id
      };
      
      // Fix: addCardToCollection expects a second boolean parameter
      const success = await addCardToCollection(card, false);
      
      if (success) {
        setIsSaved(true);
        toast({
          title: "Success",
          description: "Card added to your collection!",
        });
      }
    } catch (error) {
      console.error("Error saving card:", error);
      toast({
        title: "Error",
        description: "There was a problem saving the card. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };
  
  const handleToggleFavorite = async () => {
    if (!card || !isLoggedIn) return;
    
    setIsUpdating(true);
    try {
      const success = await toggleFavorite(card.id);
      if (success) {
        setIsFavorite(!isFavorite);
        toast({
          title: isFavorite ? "Removed from favorites" : "Added to favorites",
          description: isFavorite ? "Card removed from favorites" : "Card added to favorites!",
        });
      }
    } catch (error) {
      console.error("Error toggling favorite status:", error);
      toast({
        title: "Error",
        description: "Failed to update favorite status. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };
  
  // Updated to match ResultView.tsx for consistency
  const getCardPrice = () => {
    if (!card) return 0;
    
    // First try TCGPlayer prices
    if (card.tcgplayer?.prices) {
      // Get the first price object (normal, holofoil, etc)
      const firstPriceType = Object.keys(card.tcgplayer.prices)[0];
      if (firstPriceType && card.tcgplayer.prices[firstPriceType]?.market) {
        return card.tcgplayer.prices[firstPriceType].market;
      }
    }
    
    // Then try Cardmarket prices
    if (card.cardmarket?.prices?.averageSellPrice) {
      return card.cardmarket.prices.averageSellPrice;
    }
    
    return 0;
  };
  
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <Skeleton className="aspect-[2/3] w-full" />
        <div className="space-y-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-6 w-1/3" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
          <div className="space-y-2 pt-4">
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-24 w-full" />
          </div>
        </div>
      </div>
    );
  }
  
  if (!card) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-semibold mb-2">Card Not Found</h2>
        <p className="text-muted-foreground mb-6">
          The card you're looking for could not be found.
        </p>
        <Button onClick={() => window.history.back()}>Go Back</Button>
      </div>
    );
  }
  
  const getTcgPrice = () => {
    if (!card.tcgplayer?.prices) return null;
    return Object.values(card.tcgplayer.prices)[0];
  };
  
  const getMarketTrend = () => {
    const price = getTcgPrice();
    if (!price) return null;
    
    // Simple market trend calculation (could be more sophisticated in real app)
    const marketPrice = price.market;
    const avgPrice = (price.low + price.high) / 2;
    
    if (marketPrice > avgPrice * 1.1) return "rising";
    if (marketPrice < avgPrice * 0.9) return "falling";
    return "stable";
  };
  
  const marketTrend = getMarketTrend();
  const tcgPrice = getTcgPrice();
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div className="flex justify-center">
        <Card className="overflow-hidden w-full max-w-md">
          <CardContent className="p-0">
            <img 
              src={card.images.large} 
              alt={card.name} 
              className="w-full h-auto"
              loading="eager"
            />
          </CardContent>
        </Card>
      </div>
      
      <div>
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl md:text-3xl font-bold">{card.name}</h1>
          <Button 
            variant={isFavorite ? "default" : "outline"}
            size="icon"
            onClick={handleToggleFavorite}
            disabled={!isLoggedIn || isUpdating}
            className={isFavorite ? "bg-pokemon-blue hover:bg-pokemon-blue" : ""}
          >
            <Heart className={isFavorite ? "text-white" : ""} />
          </Button>
        </div>
        
        <div className="flex items-center space-x-2 mb-6">
          <span className="text-muted-foreground">{card.set.name}</span>
          <span className="text-xs bg-gray-100 px-2 py-1 rounded-full">
            {card.number}/{card.set.printedTotal}
          </span>
          {card.rarity && (
            <span className="text-xs bg-pokemon-blue/10 text-pokemon-blue px-2 py-1 rounded-full">
              {card.rarity}
            </span>
          )}
        </div>
        
        <Card className="mb-6">
          <CardContent className="p-4">
            <h2 className="text-lg font-semibold mb-4">Market Value</h2>
            
            {tcgPrice ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Low</p>
                    <p className="text-lg font-medium">${tcgPrice.low?.toFixed(2) || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Market</p>
                    <p className="text-lg font-medium">${tcgPrice.market?.toFixed(2) || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">High</p>
                    <p className="text-lg font-medium">${tcgPrice.high?.toFixed(2) || 'N/A'}</p>
                  </div>
                </div>
                
                {marketTrend && (
                  <div>
                    <div className="flex justify-between mb-1">
                      <p className="text-sm">Market Trend</p>
                      <p className={`text-sm font-medium ${
                        marketTrend === "rising" ? "text-green-600" : 
                        marketTrend === "falling" ? "text-red-600" : 
                        "text-amber-600"
                      }`}>
                        {marketTrend === "rising" ? "Rising" : 
                         marketTrend === "falling" ? "Falling" : 
                         "Stable"}
                      </p>
                    </div>
                    <Progress value={
                      marketTrend === "rising" ? 80 : 
                      marketTrend === "falling" ? 30 : 
                      50
                    } className={`h-2 ${
                      marketTrend === "rising" ? "bg-green-100" : 
                      marketTrend === "falling" ? "bg-red-100" : 
                      "bg-amber-100"
                    }`} />
                  </div>
                )}
                
                <div className="pt-2 text-xs text-muted-foreground">
                  Last updated: {new Date(card.tcgplayer.updatedAt).toLocaleDateString()}
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">Price data unavailable for this card.</p>
            )}
          </CardContent>
        </Card>
        
        <div className="space-y-4">
          {card.flavorText && (
            <div>
              <h2 className="text-lg font-semibold mb-2">Description</h2>
              <p className="italic text-muted-foreground">{card.flavorText}</p>
            </div>
          )}
          
          <div>
            <h2 className="text-lg font-semibold mb-2">Details</h2>
            <div className="grid grid-cols-2 gap-y-2">
              {card.artist && (
                <>
                  <span className="text-muted-foreground">Artist</span>
                  <span>{card.artist}</span>
                </>
              )}
              <span className="text-muted-foreground">Set</span>
              <span>{card.set.name}</span>
              <span className="text-muted-foreground">Series</span>
              <span>{card.set.series}</span>
              <span className="text-muted-foreground">Release Date</span>
              <span>{new Date(card.set.releaseDate).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
